import React from "react";
import { Link } from "react-router-dom";
import { Mail, MapPin, Globe, Phone, FileDown, ArrowUpRight } from "lucide-react";
import { useLang } from "../../context/LangContext";
import { COMPANY } from "../../lib/siteData";

const Footer = () => {
  const { t } = useLang();
  return (
    <footer data-testid="site-footer" className="bg-ink text-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-10 pt-20 pb-10">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-10">
          <div className="md:col-span-5">
            <img src={COMPANY.logos.transparent} alt={COMPANY.name} className="h-44 lg:h-56 w-auto mb-6 brightness-0 invert opacity-95 object-contain" data-testid="footer-logo" />
            <div className="font-heading text-2xl font-bold tracking-tight mb-2">WAVES TECHNOLOGY</div>
            <p className="text-steel-300 text-sm leading-relaxed max-w-md">
              {t("footer.tagline")}. Engineering, procurement, water treatment and project development services for the Middle East's industrial sector.
            </p>
            <p className="text-steel-500 text-xs mt-5 font-mono">{t("footer.registered")}</p>
          </div>

          <div className="md:col-span-2">
            <div className="overline text-brand mb-4">{t("footer.quickLinks")}</div>
            <ul className="space-y-2.5 text-sm text-steel-300">
              <li><Link className="hover:text-white" to="/about">{t("nav.about")}</Link></li>
              <li><Link className="hover:text-white" to="/services">{t("nav.services")}</Link></li>
              <li><Link className="hover:text-white" to="/industries">{t("nav.industries")}</Link></li>
              <li><Link className="hover:text-white" to="/projects">{t("nav.projects")}</Link></li>
              <li><Link className="hover:text-white" to="/partners">{t("nav.partners")}</Link></li>
            </ul>
          </div>

          <div className="md:col-span-2">
            <div className="overline text-brand mb-4">{t("footer.capabilities")}</div>
            <ul className="space-y-2.5 text-sm text-steel-300">
              <li>Water Treatment</li>
              <li>Engineering Support</li>
              <li>Procurement & Sourcing</li>
              <li>Vendor Coordination</li>
              <li>Project Consulting</li>
            </ul>
          </div>

          <div className="md:col-span-3">
            <div className="overline text-brand mb-4">{t("footer.contact")}</div>
            <ul className="space-y-3 text-sm text-steel-300">
              <li className="flex items-start gap-2.5">
                <MapPin className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                <span>{COMPANY.address}</span>
              </li>
              <li className="flex items-start gap-2.5">
                <Mail className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                <a href={`mailto:${COMPANY.email}`} className="hover:text-white" data-testid="footer-email">{COMPANY.email}</a>
              </li>
              <li className="flex items-start gap-2.5">
                <Phone className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                <a href={COMPANY.phoneHref} className="hover:text-white" data-testid="footer-phone">{COMPANY.phone}</a>
              </li>
              <li>
                <a
                  href={`${process.env.REACT_APP_BACKEND_URL}/api/profile.pdf`}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="footer-download-profile"
                  className="inline-flex items-center gap-2 mt-3 border border-brand hover:bg-brand text-white px-4 py-2.5 text-xs uppercase tracking-[0.18em] font-semibold transition-colors"
                >
                  <FileDown className="w-3.5 h-3.5" /> Company Profile (PDF)
                </a>
              </li>
              <li className="flex items-start gap-2.5">
                <Globe className="w-4 h-4 mt-0.5 text-brand shrink-0" />
                <a href={COMPANY.websiteHref} target="_blank" rel="noreferrer" className="hover:text-white inline-flex items-center gap-1">
                  {COMPANY.website}<ArrowUpRight className="w-3 h-3" />
                </a>
              </li>
            </ul>
            <Link
              to="/rfq"
              data-testid="footer-rfq-cta"
              className="inline-flex items-center gap-2 mt-6 bg-brand hover:bg-brand-hover text-white text-xs uppercase tracking-[0.2em] font-semibold px-5 py-3"
            >
              {t("common.submitRfq")} <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        <div className="mt-14 pt-6 border-t border-steel-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-steel-500">
          <div>© {new Date().getFullYear()} {COMPANY.name}. {t("footer.rights")}</div>
          <div className="font-mono">v 1.0 · IFZA · Dubai, UAE</div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
